import { DEFAULT_QUOTATION_EXCLUSION_OPTIONS } from '@/constants/quotationExclusions';

export const QUOTATION_PROJECT_SCOPE_OPTIONS = [
  'Structural Engineering Design and Documentation',
  'Civil Engineering Design and Documentation',
  'Structural and Civil Engineering Design and Documentation',
  'Structural and Civil Engineering Design and Documentation and Construction Phase Inspection',
  'Site Inspection',
  'Third Party Certification',
  'Protection Work Review',
  'Structural, Civil and Service Engineering Design and Documentation',
  'Civil Engineering Stormwater Management Report',
] as const;

export const QUOTATION_PROJECT_TYPE_OPTIONS = [
  'Proposed Single Dwelling',
  'Proposed Single Dwelling with Basement',
  'Proposed Multi-unit Development',
  'Proposed Multi-unit Development - 2F Townhouse Group',
  'Proposed Multi-unit Development with Basement',
  'Proposed Apartments with Basement',
  'Proposed Alteration and Addition to Existing Dwelling',
  'Proposed Shop Renovation',
  'Site Inspection',
  'Proposed Greenfield Development',
  'Proposed Childcare Centre',
  'Proposed Car park',
  'Proposed Batminton Court Floor',
] as const;

export type QuotationScopeCatalogueSection = {
  key: string;
  title: string;
  bullets: string[];
  tint: 'structural' | 'civil' | 'general';
};

export const DEFAULT_QUOTATION_SCOPE_SECTIONS: QuotationScopeCatalogueSection[] = [
  {
    key: 'additionalSiteFeatures',
    title: 'Additional site features',
    tint: 'general',
    bullets: [
      'Design and documentation of landscaping retaining wall, deck and pergola',
      'Design and documentation of front fence',
    ],
  },
  {
    key: 'twoStoreyTraditional',
    title: '2F traditional house / townhouse',
    tint: 'structural',
    bullets: [
      'Design and documentation of foundation',
      'Design and documentation of first floor lightweight framing',
      'Design and documentation of roof framing',
      'Design and documentation of wind bracing system',
      'Structural Certification of Compliance - Certificate 126 for Building Permit',
    ],
  },
  {
    key: 'threeStoreyTraditional',
    title: '3F traditional house / townhouse',
    tint: 'structural',
    bullets: [
      'Design and documentation of foundation',
      'Design and documentation of first and second floor lightweight framing',
      'Design and documentation of roof framing',
      'Design and documentation of wind bracing system',
      'Structural Certification of Compliance - Certificate 126 for Building Permit',
    ],
  },
  {
    key: 'twoStoreyWithBasement',
    title: '2F with Basement (1F 2F lightweight)',
    tint: 'structural',
    bullets: [
      'Design and documentation of basement retention system and basement structure',
      'Design and documentation of ground floor suspended slab',
      'Design and documentation of first floor lightweight framing',
      'Design and documentation of roof framing',
      'Design and documentation of wind bracing system',
      'Structural Certification of Compliance - Certificate 126 for Building Permit',
    ],
  },
  {
    key: 'threeStoreyWithBasementGroundConcrete',
    title: '3F with Basement (GF Concrete 1/2F lightweight)',
    tint: 'structural',
    bullets: [
      'Design and documentation of basement soil retention structure (wet basement)',
      'Design and documentation of ground floor suspended slab and supporting structure',
      'Design and documentation of first, second floor and roof lightweight structure',
      'Design and documentation of wind bracing design',
      'Design and documentation of site retaining walls',
      'Structural Certification of Compliance - Certificate 126 for Building Permit',
    ],
  },
  {
    key: 'twoStoreyHouseAlteration',
    title: '2F House Alteration',
    tint: 'structural',
    bullets: [
      'Design and documentation of existing building internal framing alteration',
      'Design and documentation of rear extension foundation',
      'Design and documentation of rear extension first floor framing',
      'Design and documentation of rear extension roof framing',
      'Design and documentation of wind bracing',
      'Structural Certification of Compliance - Certificate 126 for Building Permit',
      'One (1) site inspection',
    ],
  },
  {
    key: 'threeStoreyWithBasementConcrete',
    title: '3F with Basement (1F 2F concrete 3F lightweight)',
    tint: 'structural',
    bullets: [
      'Design and documentation of basement soil retention structure',
      'Design and documentation of ground floor suspended slab and supporting structure',
      'Design and documentation of first and second floor suspended slab and supporting structure',
      'Design and documentation of third floor and roof lightweight timber structure',
      'Design and documentation of wind bracing design',
      'Structural Certification of Compliance - Certificate 126 for Building Permit',
      'Three (3) design meetings',
    ],
  },
  {
    key: 'cfsReview',
    title: 'CFS',
    tint: 'structural',
    bullets: [
      'Checking and verify cold-form steel roof, wall stud / lintel design by manufacturer - DeepBlue',
      'Checking and verify cold-form steel floor joist design by manufacturer - DeepBlue',
      'Checking and verify cold-form steel bracing design by manufacturer - DeepBlue',
      'Structural Certification of Compliance - Certificate 126 for Building Permit',
    ],
  },
  {
    key: 'coldFormedSteelScope',
    title: 'Cold Formed Steel Scope',
    tint: 'structural',
    bullets: [
      'Checking and verify cold-form steel roof, wall stud / lintel design by manufacturer - DeepBlue',
      'Checking and verify cold-form steel bracing design by manufacturer - DeepBlue',
      'Design and documentation of additional structural steel and connection to CFS as required',
      'Design and documentation of foundation',
      'Design and documentation of site retaining wall',
      'Structural Certification of Compliance - Certificate 126 for Building Permit',
    ],
  },
  {
    key: 'civilStandard',
    title: 'Civil Standard',
    tint: 'civil',
    bullets: [
      'Civil design and documentation for proposed development',
      'All storm water drainage external to the building above ground',
      'Onsite Detention System and discharge control system as required',
      'Civil Drainage Certification of Compliance - Certificate 126 for Building Permit',
    ],
  },
  {
    key: 'civilWithBasement',
    title: 'Civil with Basement',
    tint: 'civil',
    bullets: [
      'Onsite Stormwater Detention and documentation',
      'Internal drainage elements (pits and pipes) and paving slab extent and detailing',
      'Coordination with council to ensure compliance with authority requirements',
      'Civil Drainage Certification of Compliance - Certificate 126 for Building Permit',
    ],
  },
  {
    key: 'civilDwellingExtension',
    title: 'Civil Dwelling Extension / Single Dwelling',
    tint: 'civil',
    bullets: [
      'Civil design and documentation for proposed extension',
      'All storm water drainage external to the building above ground',
      'Civil Drainage Certification of Compliance - Certificate 126 for Building Permit',
    ],
  },
  {
    key: 'civilCarPark',
    title: 'Civil Car Park / Access',
    tint: 'civil',
    bullets: [
      'Design and documentation for underground drainage network',
      'Design and documentation for internal carpark',
      'Details including levels of driveways, common driveway and parking areas within the site',
      'Design and computation of onsite detention as per council requirements',
      'Design to WSUD requirements as per Energy Rating report as required',
      'Civil Drainage Certification of Compliance - Certificate 126 for Building Permit',
    ],
  },
  {
    key: 'earthworksAndSurface',
    title: 'Earthworks / 3D surface',
    tint: 'civil',
    bullets: [
      'Design and documentation for earthworks',
      '3D surface for entire site assisting the service engineers (required for authorities works, including sewer and water)',
    ],
  },
  {
    key: 'warehouseStructural',
    title: 'Commercial / warehouse structural scope',
    tint: 'structural',
    bullets: [
      'Design and documentation of warehouse foundation',
      'Design and documentation of portal frame and roof framing',
      'Design and documentation of canopy frame',
      'Design and documentation of precast panels and connections',
      'Structural Certification of Compliance - Certificate 126 for Building Permit',
    ],
  },
  {
    key: 'siteInspection',
    title: 'Site Inspection',
    tint: 'general',
    bullets: [
      'One site inspection and written engineering observations for the nominated works',
    ],
  },
  {
    key: 'thirdPartyCertification',
    title: 'Third Party Certification',
    tint: 'general',
    bullets: [
      'Independent review of supplied design documents and certification advice for building permit support',
      'Clarification requests and final certification letter based on reviewed documents',
    ],
  },
  {
    key: 'constructionPhaseInspection',
    title: 'Construction Phase Inspection',
    tint: 'general',
    bullets: [
      'Construction phase inspection attendance as required by the approved engineering documentation',
    ],
  },
];

export const DEFAULT_QUOTATION_STRUCTURAL_TYPE_SCOPE_MAP: Record<string, string[]> = {
  'proposed single dwelling': ['additionalSiteFeatures', 'twoStoreyTraditional'],
  'proposed single dwelling with basement': ['additionalSiteFeatures', 'twoStoreyWithBasement'],
  'proposed multi-unit development': ['threeStoreyTraditional'],
  'proposed multi-unit development - 2f townhouse group': ['twoStoreyTraditional'],
  'proposed multi-unit development with basement': ['threeStoreyWithBasementGroundConcrete'],
  'proposed apartments with basement': ['threeStoreyWithBasementConcrete'],
  'proposed alteration and addition to existing dwelling': ['additionalSiteFeatures', 'twoStoreyHouseAlteration'],
  'proposed shop renovation': ['cfsReview', 'coldFormedSteelScope'],
  'site inspection': ['siteInspection'],
  'proposed childcare centre': ['coldFormedSteelScope'],
  'proposed car park': ['warehouseStructural'],
  'proposed batminton court floor': ['coldFormedSteelScope'],
};

export const DEFAULT_QUOTATION_CIVIL_TYPE_SCOPE_MAP: Record<string, string[]> = {
  'proposed single dwelling': ['civilDwellingExtension'],
  'proposed single dwelling with basement': ['civilWithBasement'],
  'proposed multi-unit development': ['civilStandard'],
  'proposed multi-unit development with basement': ['civilWithBasement'],
  'proposed apartments with basement': ['civilWithBasement'],
  'proposed alteration and addition to existing dwelling': ['civilDwellingExtension'],
  'site inspection': ['siteInspection'],
  'proposed greenfield development': ['civilStandard', 'earthworksAndSurface'],
  'proposed childcare centre': ['civilStandard'],
  'proposed car park': ['civilCarPark'],
  'proposed batminton court floor': ['civilStandard'],
};

export type QuotationTemplateDefinition = {
  templateCode: string;
  templateName: string;
  versionNo: number;
  projectScopeOptions: string[];
  projectTypeOptions: string[];
  scopeSections: QuotationScopeCatalogueSection[];
  structuralTypeScopeMap: Record<string, string[]>;
  civilTypeScopeMap: Record<string, string[]>;
  exclusionOptions: string[];
  termsConditions: string[];
};

export const DEFAULT_QUOTATION_TEMPLATE_DEFINITION: QuotationTemplateDefinition = {
  templateCode: 'quotation-default',
  templateName: 'Quotation Default',
  versionNo: 1,
  projectScopeOptions: [...QUOTATION_PROJECT_SCOPE_OPTIONS],
  projectTypeOptions: [...QUOTATION_PROJECT_TYPE_OPTIONS],
  scopeSections: DEFAULT_QUOTATION_SCOPE_SECTIONS.map((section) => ({
    ...section,
    bullets: [...section.bullets],
  })),
  structuralTypeScopeMap: Object.fromEntries(
    Object.entries(DEFAULT_QUOTATION_STRUCTURAL_TYPE_SCOPE_MAP).map(([key, value]) => [key, [...value]])
  ),
  civilTypeScopeMap: Object.fromEntries(
    Object.entries(DEFAULT_QUOTATION_CIVIL_TYPE_SCOPE_MAP).map(([key, value]) => [key, [...value]])
  ),
  exclusionOptions: [...DEFAULT_QUOTATION_EXCLUSION_OPTIONS],
  termsConditions: [],
};

function normalizeKey(value?: string | null): string {
  return String(value || '').trim().toLowerCase();
}

function shouldIncludeStructuralScope(projectScope?: string | null): boolean {
  const normalized = normalizeKey(projectScope);
  if (!normalized) return false;
  if (normalized === 'site inspection' || normalized === 'third party certification') return false;
  return normalized.includes('structural');
}

function shouldIncludeCivilScope(projectScope?: string | null): boolean {
  const normalized = normalizeKey(projectScope);
  if (!normalized) return false;
  return normalized.includes('civil') || normalized.includes('stormwater');
}

function shouldUseTwoStoreyMultiUnitScope(projectType?: string | null, projectDescription?: string | null): boolean {
  const normalizedType = normalizeKey(projectType);
  const normalizedDescription = normalizeKey(projectDescription);
  if (!normalizedType.includes('multi-unit')) return false;
  if (normalizedType.includes('2f') || normalizedType.includes('two')) return true;
  return /\b(2f|2\s*f|two|double)\b/.test(normalizedDescription)
    && /\b(storey|story|townhouse|unit|dwelling)s?\b/.test(normalizedDescription);
}

export function getQuotationScopeCatalogueSelectionFromTemplate(
  template: Pick<QuotationTemplateDefinition, 'scopeSections' | 'structuralTypeScopeMap' | 'civilTypeScopeMap'>,
  input: {
    projectScope?: string | null;
    projectType?: string | null;
    projectDescription?: string | null;
  }
): QuotationScopeCatalogueSection[] {
  const normalizedScope = normalizeKey(input.projectScope);
  const normalizedType = normalizeKey(input.projectType);
  const sectionMap = new Map(
    (template.scopeSections || []).map((section) => [section.key, section])
  );
  const structuralTypeScopeMap = template.structuralTypeScopeMap || {};
  const civilTypeScopeMap = template.civilTypeScopeMap || {};
  const sectionKeys: string[] = [];

  if (normalizedScope === 'site inspection') {
    sectionKeys.push('siteInspection');
  } else if (normalizedScope === 'third party certification') {
    sectionKeys.push('thirdPartyCertification');
  } else {
    if (shouldIncludeStructuralScope(normalizedScope)) {
      const structuralKeys = shouldUseTwoStoreyMultiUnitScope(input.projectType, input.projectDescription)
        ? ['twoStoreyTraditional']
        : (structuralTypeScopeMap[normalizedType] || ['twoStoreyTraditional']);
      sectionKeys.push(...structuralKeys);
    }

    if (shouldIncludeCivilScope(normalizedScope)) {
      sectionKeys.push(...(civilTypeScopeMap[normalizedType] || ['civilStandard']));
    }

    if (normalizedScope.includes('construction phase inspection')) {
      sectionKeys.push('constructionPhaseInspection');
    }
  }

  if (normalizedScope.includes('service engineering')) {
    sectionKeys.push('earthworksAndSurface');
  }

  const seen = new Set<string>();
  return sectionKeys
    .filter((key) => {
      if (!sectionMap.has(key) || seen.has(key)) return false;
      seen.add(key);
      return true;
    })
    .map((key) => sectionMap.get(key)!)
    .map((section) => ({
      ...section,
      bullets: [...section.bullets],
    }));
}

export function getQuotationScopeCatalogueSelection(input: {
  projectScope?: string | null;
  projectType?: string | null;
  projectDescription?: string | null;
}): QuotationScopeCatalogueSection[] {
  return getQuotationScopeCatalogueSelectionFromTemplate(DEFAULT_QUOTATION_TEMPLATE_DEFINITION, input);
}
